import axios from 'axios'

import {
  type AttendanceActionItem,
  type AttendanceActionKind,
  type AttendanceActionPayload,
  deleteAttendanceAction,
  getAllAttendanceActions,
  putAttendanceAction,
} from '@/lib/action-outbox'
import { api } from '@/lib/api'
import { queryClient } from '@/lib/query-client'

type ApiErrorLike = {
  message?: unknown
  response?: {
    data?: {
      message?: unknown
      error?: unknown
    }
  }
}

export interface QueuedAttendanceAction {
  queued: true
  actionId: string
}

let items: AttendanceActionItem[] = []
let initialized = false
let flushing = false

function newActionId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID()
  }
  return `aa_${Date.now()}_${Math.random().toString(36).slice(2)}`
}

function isNetworkError(error: unknown): boolean {
  if (typeof navigator !== 'undefined' && !navigator.onLine) return true
  return axios.isAxiosError(error) && !error.response
}

function failureReasonFromError(error: unknown): string {
  if (typeof error !== 'object' || error === null) {
    return 'Erro inesperado ao sincronizar'
  }

  const candidate = error as ApiErrorLike
  const message =
    candidate.response?.data?.message ??
    candidate.response?.data?.error ??
    candidate.message

  if (Array.isArray(message)) {
    return String(message[0] ?? 'Erro inesperado ao sincronizar')
  }

  if (typeof message === 'string' && message.trim()) {
    return message.trim()
  }

  return 'Erro inesperado ao sincronizar'
}

function actionUrl(item: AttendanceActionItem): string {
  const base = `/orgs/${item.orgId}/attendances/${item.conversationId}`
  if (item.kind === 'assume') return `${base}/assume`
  if (item.kind === 'assign') return `${base}/assign`
  if (item.kind === 'transfer') return `${base}/transfer`
  if (item.kind === 'finalize') return `${base}/finalize`
  if (item.kind === 'archive') return `${base}/archive`
  return base
}

async function sendAction(item: AttendanceActionItem): Promise<void> {
  const url = actionUrl(item)
  if (item.kind === 'update') {
    await api.put(url, item.payload ?? {})
    return
  }
  await api.post(url, item.payload ?? {})
}

function invalidateAction(item: AttendanceActionItem) {
  void queryClient.invalidateQueries({
    queryKey: ['conversations', item.orgId],
  })
  void queryClient.invalidateQueries({
    queryKey: ['conversation', item.orgId, item.conversationId],
  })
}

export function isQueuedAttendanceAction(
  value: unknown
): value is QueuedAttendanceAction {
  return (
    typeof value === 'object' &&
    value !== null &&
    (value as { queued?: unknown }).queued === true
  )
}

export async function initActionOutbox(): Promise<void> {
  if (initialized) return
  initialized = true
  const stored = await getAllAttendanceActions()
  items = stored.map((item) =>
    item.status === 'sending' ? { ...item, status: 'queued' } : item
  )
  await Promise.all(items.map((item) => putAttendanceAction(item)))
  void flushActionOutbox()
}

export async function enqueueAttendanceAction(input: {
  orgId: string
  conversationId: string
  kind: AttendanceActionKind
  payload?: AttendanceActionPayload
}): Promise<QueuedAttendanceAction> {
  const item: AttendanceActionItem = {
    id: newActionId(),
    orgId: input.orgId,
    conversationId: input.conversationId,
    kind: input.kind,
    payload: input.payload ?? null,
    createdAt: new Date().toISOString(),
    status: 'queued',
  }

  await putAttendanceAction(item)
  items = [...items, item]
  void flushActionOutbox()

  return { queued: true, actionId: item.id }
}

export async function runOrQueueAttendanceAction<T>(
  input: {
    orgId: string | null
    conversationId: string | null
    kind: AttendanceActionKind
    payload?: AttendanceActionPayload
  },
  action: () => Promise<T>
): Promise<T | QueuedAttendanceAction> {
  if (!input.orgId || !input.conversationId) {
    return action()
  }

  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    return enqueueAttendanceAction({
      orgId: input.orgId,
      conversationId: input.conversationId,
      kind: input.kind,
      payload: input.payload,
    })
  }

  try {
    return await action()
  } catch (error) {
    if (!isNetworkError(error)) throw error
    return enqueueAttendanceAction({
      orgId: input.orgId,
      conversationId: input.conversationId,
      kind: input.kind,
      payload: input.payload,
    })
  }
}

export async function flushActionOutbox(): Promise<void> {
  if (flushing) return
  if (typeof navigator !== 'undefined' && !navigator.onLine) return
  flushing = true

  try {
    const pending = items.filter((item) => item.status === 'queued')

    for (const item of pending) {
      const sending = { ...item, status: 'sending' as const }
      await putAttendanceAction(sending)
      items = items.map((current) =>
        current.id === item.id ? sending : current
      )

      try {
        await sendAction(item)
        await deleteAttendanceAction(item.id)
        items = items.filter((current) => current.id !== item.id)
        invalidateAction(item)
      } catch (error) {
        if (isNetworkError(error)) {
          const queued = { ...item, status: 'queued' as const }
          await putAttendanceAction(queued)
          items = items.map((current) =>
            current.id === item.id ? queued : current
          )
          break
        }

        const failed = {
          ...item,
          status: 'failed' as const,
          failureReason: failureReasonFromError(error),
        }
        await putAttendanceAction(failed)
        items = items.map((current) =>
          current.id === item.id ? failed : current
        )
        // Keep failed actions persisted for a future diagnostics surface.
        console.warn('[ActionOutbox] Falha ao sincronizar ação', failed)
      }
    }
  } finally {
    flushing = false
  }
}

export function startActionOutboxAutoFlush(): void {
  if (typeof window === 'undefined') return
  window.addEventListener('online', () => void flushActionOutbox())
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void flushActionOutbox()
  })
}
